import { useState } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { btnGhost, btnPrimary } from "./ui";

const FIELDS = [["subheadline", "Podnaslov"], ["summary", "Sažetak"], ["seo_title", "SEO naslov"], ["seo_description", "SEO opis"], ["tags", "Oznake"]];
const DIST = [["facebook", "Facebook"], ["instagram", "Instagram"], ["whatsapp", "WhatsApp"], ["newsletter", "Newsletter"], ["push", "Push"]];

const Row = ({ k, label, current, value, on, toggle }) => (
  <label className="block rounded border border-border p-3 text-sm" data-testid={`ai-row-${k}`}>
    <span className="flex items-center gap-2 font-semibold"><input type="checkbox" checked={on} onChange={toggle} data-testid={`ai-accept-${k}`} /> {label}</span>
    {current && <span className="mt-1 block text-xs text-muted-foreground line-through">{current}</span>}
    <span className="mt-1 block whitespace-pre-wrap">{value}</span>
  </label>
);

export const AiSuggest = ({ articleId, f, tagText, dirty, editable, apply }) => {
  const [s, setS] = useState(null);
  const [on, setOn] = useState({});
  const [headline, setHeadline] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (dirty) return toast.error("Prvo spremite izmjene – AI čita spremljeni tekst članka.");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/articles/${articleId}/ai-suggest`);
      setS(data);
      setHeadline("");
      setOn(Object.fromEntries([...FIELDS.map(([k]) => [k, true]), ...DIST.map(([k]) => [`d_${k}`, true])]));
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const accept = () => {
    const next = { ...f, distribution: { ...f.distribution } };
    if (headline) next.headline = headline;
    FIELDS.forEach(([k]) => { if (on[k] && k !== "tags" && s[k]) next[k] = s[k]; });
    DIST.forEach(([k]) => { if (on[`d_${k}`] && s.distribution[k]) next.distribution[k] = s.distribution[k]; });
    const tags = on.tags ? [...new Set([...tagText.split(",").map((t) => t.trim()).filter(Boolean), ...s.tags])].join(", ") : tagText;
    apply(next, tags);
    setS(null);
    toast.success("Prijedlozi preneseni u formular – pregledajte i sačuvajte članak.");
  };
  if (!editable) return null;
  return (
    <>
      <button type="button" onClick={run} disabled={busy} className={`${btnGhost} w-full`} data-testid="ai-suggest-button"><Sparkles className="h-4 w-4 text-pulse" /> {busy ? "AI analizira tekst…" : "AI prijedlog: SEO, sažeci, oznake"}</button>
      {s && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="ai-suggest-dialog">
          <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-card p-5">
            <h2 className="font-display text-xl font-semibold">AI prijedlog</h2>
            <p className="mb-4 text-xs text-muted-foreground">Generisano isključivo iz spremljenog teksta članka. Ništa se ne mijenja dok ne kliknete „Prihvati odabrano“. Provjerite tačnost prije objave.</p>
            <div className="space-y-2">
              {s.headlines?.length > 0 && (
                <fieldset className="rounded border border-border p-3 text-sm" data-testid="ai-headlines">
                  <legend className="px-1 font-semibold">Alternativni naslov (odaberite jedan)</legend>
                  <label className="flex items-start gap-2 py-1"><input type="radio" name="ai-h" checked={!headline} onChange={() => setHeadline("")} data-testid="ai-headline-keep" /> <span className="text-muted-foreground">Zadrži trenutni: {f.headline}</span></label>
                  {s.headlines.map((h, i) => (
                    <label key={i} className="flex items-start gap-2 py-1"><input type="radio" name="ai-h" checked={headline === h} onChange={() => setHeadline(h)} data-testid={`ai-headline-${i}`} /> <span>{h}</span></label>
                  ))}
                </fieldset>
              )}
              {FIELDS.map(([k, l]) => <Row key={k} k={k} label={l} current={k === "tags" ? tagText : f[k]} value={k === "tags" ? s.tags.join(", ") : s[k]} on={!!on[k]} toggle={() => setOn({ ...on, [k]: !on[k] })} />)}
              {DIST.map(([k, l]) => <Row key={k} k={`d_${k}`} label={`Distribucija · ${l}`} current={f.distribution?.[k]} value={s.distribution[k]} on={!!on[`d_${k}`]} toggle={() => setOn({ ...on, [`d_${k}`]: !on[`d_${k}`] })} />)}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setS(null)} className={btnGhost} data-testid="ai-suggest-cancel">Odbaci</button>
              <button type="button" onClick={accept} className={btnPrimary} data-testid="ai-suggest-accept">Prihvati odabrano</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
